import React from 'react';
import { formatDate } from '../services/expirationMonitor';
import '../global/styles.css';

// Sleek SVG Icons
const AlertIcon = ({ status }) => {
  const color = status === 'critical' ? '#dc3545' : '#ffc107';

  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ marginRight: '8px', flexShrink: 0 }}
    >
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
};

const CloseIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const CalendarIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ marginRight: '4px', opacity: 0.7 }}
  >
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

function ExpirationModal({ locationData, onClose }) {
  if (!locationData || !locationData.medications || locationData.medications.length === 0) {
    return null;
  }

  const getDaysText = (daysUntil) => {
    if (daysUntil === 0) return 'Expires today';
    if (daysUntil < 0) return `Expired ${Math.abs(daysUntil)} day${Math.abs(daysUntil) !== 1 ? 's' : ''} ago`;
    return `${daysUntil} day${daysUntil !== 1 ? 's' : ''} remaining`;
  };

  const isMobile = window.innerWidth <= 768;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.75)',
      display: 'flex',
      alignItems: isMobile ? 'flex-end' : 'center',
      justifyContent: 'center',
      zIndex: 10000,
      padding: isMobile ? '0' : '20px'
    }}>
      <div style={{
        background: 'white',
        borderRadius: isMobile ? '16px 16px 0 0' : '16px',
        padding: '0',
        maxWidth: isMobile ? '100%' : '480px',
        width: '100%',
        maxHeight: isMobile ? '90vh' : '85vh',
        overflow: 'hidden',
        boxShadow: '0 12px 40px rgba(0, 0, 0, 0.3)',
        display: 'flex',
        flexDirection: 'column',
        paddingBottom: isMobile ? 'env(safe-area-inset-bottom)' : '0'
      }}>
        {/* Header */}
        <div style={{
          padding: isMobile ? '20px 20px 16px' : '20px 24px 16px',
          borderBottom: '1px solid #e0e0e0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white'
        }}>
          <div>
            <h2 style={{
              margin: 0,
              fontSize: isMobile ? '18px' : '20px',
              fontWeight: '600',
              marginBottom: '4px'
            }}>
              {locationData.location.name}
            </h2>
            <p style={{
              margin: 0,
              fontSize: isMobile ? '12px' : '13px',
              opacity: 0.9
            }}>
              Expiration Alert
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              width: isMobile ? '40px' : '32px',
              height: isMobile ? '40px' : '32px',
              borderRadius: '50%',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              transition: 'all 0.2s ease',
              WebkitTapHighlightColor: 'transparent'
            }}
            onMouseEnter={(e) => {
              if (!isMobile) e.target.style.background = 'rgba(255, 255, 255, 0.3)';
            }}
            onMouseLeave={(e) => {
              if (!isMobile) e.target.style.background = 'rgba(255, 255, 255, 0.2)';
            }}
          >
            <CloseIcon />
          </button>
        </div>

        {/* Content */}
        <div style={{
          padding: isMobile ? '16px 20px' : '20px 24px',
          overflowY: 'auto',
          flex: 1,
          WebkitOverflowScrolling: 'touch'
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '12px' : '10px' }}>
            {locationData.medications.map((med, index) => (
              <div
                key={index}
                style={{
                  padding: isMobile ? '16px' : '14px 16px',
                  background: med.status === 'critical' ? '#fff5f5' : '#fffbf0',
                  border: `1.5px solid ${med.status === 'critical' ? '#dc3545' : '#ffc107'}`,
                  borderRadius: '10px',
                  transition: 'all 0.2s ease'
                }}
              >
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '10px'
                }}>
                  <AlertIcon status={med.status} />
                  <div style={{ flex: 1 }}>
                    <h3 style={{
                      margin: 0,
                      fontSize: isMobile ? '16px' : '15px',
                      fontWeight: '600',
                      color: '#333',
                      marginBottom: '6px'
                    }}>
                      {med.name}
                    </h3>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      fontSize: isMobile ? '13px' : '12px',
                      color: '#666',
                      marginBottom: '8px'
                    }}>
                      <CalendarIcon />
                      <span>Expires: {formatDate(med.expirationDate)}</span>
                    </div>
                    <div style={{
                      fontSize: isMobile ? '14px' : '13px',
                      fontWeight: '600',
                      color: med.status === 'critical' ? '#dc3545' : '#f57c00',
                      padding: isMobile ? '6px 12px' : '4px 10px',
                      background: 'white',
                      borderRadius: '5px',
                      display: 'inline-block'
                    }}>
                      {getDaysText(med.daysUntil)}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: isMobile ? '16px 20px' : '16px 24px',
          borderTop: '1px solid #e0e0e0',
          background: '#f8f9fa',
          display: 'flex',
          justifyContent: 'flex-end'
        }}>
          <button
            className="btn btn-primary"
            onClick={onClose}
            style={{
              padding: isMobile ? '12px 28px' : '10px 24px',
              background: '#667eea',
              borderColor: '#667eea',
              fontWeight: '600',
              fontSize: isMobile ? '15px' : '14px',
              minHeight: isMobile ? '44px' : 'auto',
              WebkitTapHighlightColor: 'transparent'
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default ExpirationModal;
